import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Minus, Plus, X, ShoppingBag, Tag, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SlideOutCart() {
  const navigate = useNavigate();
  const {
    items, isOpen, closeCart, removeItem, updateQuantity,
    subtotal, total, shipping, bundleDiscount, couponDiscount, coupon, freeShipping,
    itemCount, loading, applyCoupon, removeCoupon,
  } = useCart();
  const { formatPrice } = useCurrency();
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    const result = await applyCoupon(couponCode.trim());
    if (result.error) toast.error(result.error);
    else { toast.success('Coupon applied!'); setCouponCode(''); }
    setCouponLoading(false);
  };

  const handleCheckout = () => {
    closeCart();
    navigate('/cart');
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent side="right" data-testid="slide-out-cart"
        className="w-full sm:max-w-[420px] flex flex-col p-0 border-l"
        style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
        <SheetHeader className="px-6 pt-6 pb-4 border-b" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
          <SheetTitle className="font-serif text-xl flex items-center gap-2" style={{ color: '#1A1A1A' }}>
            <ShoppingBag size={18} strokeWidth={1.5} />
            Your Cart ({itemCount})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <ShoppingBag size={40} strokeWidth={1} style={{ color: 'rgba(26,26,26,0.15)' }} />
            <p className="font-serif text-lg mt-4 mb-1" style={{ color: '#1A1A1A' }}>Your cart is empty</p>
            <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Add items to get started</p>
            <Button data-testid="cart-start-shopping" onClick={() => { closeCart(); navigate('/shop'); }}
              variant="outline" className="mt-6 rounded-none font-sans text-xs" style={{ borderColor: 'rgba(26,26,26,0.15)' }}>
              Start Shopping
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map(item => (
                <div key={item.item_id} data-testid={`cart-item-${item.item_id}`}
                  className="flex gap-4 pb-4 border-b" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                  <Link to={`/product/${item.product_handle}`} onClick={closeCart}
                    className="w-16 h-20 flex-shrink-0 overflow-hidden block" style={{ backgroundColor: 'rgba(26,26,26,0.04)' }}>
                    {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div className="min-w-0">
                        <h4 className="font-serif text-sm truncate" style={{ color: '#1A1A1A' }}>{item.title}</h4>
                        {item.variant_title && <p className="font-sans text-[11px] mt-0.5" style={{ color: 'rgba(26,26,26,0.45)' }}>{item.variant_title}</p>}
                      </div>
                      <button data-testid={`cart-remove-${item.item_id}`} onClick={() => removeItem(item.item_id)}
                        className="p-1 rounded-full transition-colors hover:bg-[rgba(26,26,26,0.05)]">
                        <X size={14} strokeWidth={1.5} style={{ color: 'rgba(26,26,26,0.4)' }} />
                      </button>
                    </div>
                    <p className="font-sans text-sm mt-1" style={{ color: 'rgba(26,26,26,0.6)' }}>{formatPrice(item.unit_price)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button data-testid={`cart-minus-${item.item_id}`} onClick={() => updateQuantity(item.item_id, item.quantity - 1)}
                        className="w-7 h-7 rounded-full border flex items-center justify-center transition-colors hover:bg-[rgba(26,26,26,0.05)]"
                        style={{ borderColor: 'rgba(26,26,26,0.15)' }}>
                        <Minus size={12} strokeWidth={1.5} />
                      </button>
                      <span className="font-sans text-sm w-6 text-center" style={{ color: '#1A1A1A' }}>{item.quantity}</span>
                      <button data-testid={`cart-plus-${item.item_id}`} onClick={() => updateQuantity(item.item_id, item.quantity + 1)}
                        className="w-7 h-7 rounded-full border flex items-center justify-center transition-colors hover:bg-[rgba(26,26,26,0.05)]"
                        style={{ borderColor: 'rgba(26,26,26,0.15)' }}>
                        <Plus size={12} strokeWidth={1.5} />
                      </button>
                      <span className="ml-auto font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>
                        {formatPrice(item.unit_price * item.quantity)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-5 border-t space-y-3" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
              {/* Coupon */}
              {!coupon ? (
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(26,26,26,0.3)' }} />
                    <Input data-testid="coupon-input" value={couponCode} onChange={e => setCouponCode(e.target.value)}
                      placeholder="Coupon code" className="pl-9 font-sans text-xs rounded-none h-9"
                      style={{ borderColor: 'rgba(26,26,26,0.12)' }}
                      onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()} />
                  </div>
                  <Button data-testid="apply-coupon-btn" onClick={handleApplyCoupon} disabled={couponLoading}
                    variant="outline" className="rounded-none font-sans text-xs h-9 px-3" style={{ borderColor: 'rgba(26,26,26,0.15)' }}>
                    {couponLoading ? <Loader2 size={13} className="animate-spin" /> : 'Apply'}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between py-1.5 px-3 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.06)' }}>
                  <span className="font-sans text-xs font-medium" style={{ color: '#16a34a' }}>
                    <Tag size={11} className="inline mr-1" />{coupon.code}
                  </span>
                  <button data-testid="remove-coupon-btn" onClick={removeCoupon} className="font-sans text-[10px] underline" style={{ color: 'rgba(26,26,26,0.4)' }}>
                    Remove
                  </button>
                </div>
              )}

              {/* Totals */}
              <div className="space-y-1.5 pt-2">
                <div className="flex justify-between">
                  <span className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>Subtotal</span>
                  <span className="font-sans text-xs" style={{ color: '#1A1A1A' }}>{formatPrice(subtotal)}</span>
                </div>
                {bundleDiscount > 0 && (
                  <div className="flex justify-between">
                    <span className="font-sans text-xs" style={{ color: '#16a34a' }}>Bundle Savings</span>
                    <span className="font-sans text-xs" style={{ color: '#16a34a' }}>-{formatPrice(bundleDiscount)}</span>
                  </div>
                )}
                {couponDiscount > 0 && (
                  <div className="flex justify-between">
                    <span className="font-sans text-xs" style={{ color: '#16a34a' }}>Discount ({coupon?.code})</span>
                    <span className="font-sans text-xs" style={{ color: '#16a34a' }}>-{formatPrice(couponDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>Shipping</span>
                  <span className="font-sans text-xs" style={{ color: freeShipping ? '#16a34a' : '#1A1A1A' }}>
                    {freeShipping ? 'FREE' : formatPrice(shipping)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                  <span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>Total</span>
                  <span className="font-serif text-lg font-medium" style={{ color: '#1A1A1A' }}>{formatPrice(total)}</span>
                </div>
              </div>

              <Button data-testid="cart-checkout-button" onClick={handleCheckout} disabled={loading}
                className="w-full h-12 rounded-none font-sans text-sm uppercase tracking-wider font-medium"
                style={{ backgroundColor: '#000', color: '#FFFFFF' }}>
                {loading ? <Loader2 size={16} className="animate-spin" /> : 'Checkout'}
              </Button>
              <button data-testid="cart-continue-btn" onClick={closeCart}
                className="w-full font-sans text-xs text-center underline underline-offset-4 transition-colors"
                style={{ color: 'rgba(26,26,26,0.5)' }}>
                Continue Shopping
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
